import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as S from '../styles';

import OutboxIcon from '@mui/icons-material/Outbox';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { ISubjectContentData } from '../../../..';
import { useTheme } from 'styled-components';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../../ProviderNewStudent';
import {
  myCoursesServiceNewStudent,
} from '../../../../../../../../services/newStudent/myCourses/myCourses.service';
import type {
  IContentSubject,
  IContentSubjectGraduation,
  IDataContentSubject,
} from '../../../../../../../../types/newStudent/myCourses.interface';
import { IClassInfo } from '../../../../../../../../pages/newStudent/MyCoursesNewStudent/components/Subjects';

import Modal from '@mui/material/Modal';
import { Box } from '@mui/material';

const hasValidUrl = (item: IDataContentSubject): boolean =>
  Boolean(item?.url && item.url.trim() !== '');

const filterWithValidUrl = (
  items: IDataContentSubject[] | undefined,
): IDataContentSubject[] => (items ?? []).filter(hasValidUrl);

export type ResourceDisplayKey =
  | 'video'
  | 'archive'
  | 'audiobook'
  | 'customized'
  | 'externalLink'
  | 'tcc'
  | 'internship'
  | 'supplementaryMaterial'
  | 'assessmentWork'
  | 'evaluativeActivity';

const NO_URL_REQUIRED_KEYS: ResourceDisplayKey[] = [
  'customized',
  'internship',
  'tcc',
  'assessmentWork',
  'evaluativeActivity',
];

const getDisplayItems = (
  key: ResourceDisplayKey,
  items: IDataContentSubject[] | undefined,
): IDataContentSubject[] => {
  const list = items ?? [];
  if (NO_URL_REQUIRED_KEYS.includes(key)) {
    return list;
  }
  return filterWithValidUrl(list);
};

interface IIdShouldDisplay {
  video?: number;
  archive?: number;
  audiobook?: number;
  customized?: number;
  tcc?: number;
  externalLink?: number;
  internship?: number;
  supplementaryMaterial?: number;
  assessmentWork?: number;
  evaluativeActivity?: number;
}

interface Props {
  setSubjectContent?: Dispatch<SetStateAction<ISubjectContentData | undefined>>;
  selectedContent?: ISubjectContentData;
  classInfo: IClassInfo;
  contentIdSelected: number;
  setBlockedContent: Dispatch<SetStateAction<boolean>>;
  visibleResourceKeys?: ResourceDisplayKey[];
}

const allResourceKeys: ResourceDisplayKey[] = [
  'video',
  'archive',
  'audiobook',
  'customized',
  'externalLink',
  'tcc',
  'internship',
  'supplementaryMaterial',
  'assessmentWork',
  'evaluativeActivity',
];

export default function TabLesson({
  setSubjectContent,
  selectedContent,
  classInfo,
  contentIdSelected,
  setBlockedContent,
  visibleResourceKeys,
}: Props) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const theme = useTheme();
  const [content, setContent] = useState<
    IContentSubject | IContentSubjectGraduation[]
  >();
  const [contentShouldDisplay, setContentShouldDisplay] =
    useState<IIdShouldDisplay>();
  const [contentOpen, setContentOpen] = useState<number>();
  const [unitOpen, setUnitOpen] = useState<number>();
  const [classOpen, setClassOpen] = useState<number>();

  const courseId = classInfo.courseId;
  const disciplineId = classInfo.id;
  const contentId = contentIdSelected;

  const [openSupplementaryModal, setOpenSupplementaryModal] = useState(false);
  const [modalItem, setModalItem] = useState<IDataContentSubject | null>(null);
  const selectedAudioUrlRef = useRef<string | undefined>();

  const handleOpenSupplementaryModal = async (item: IDataContentSubject) => {
    const { result, data } = await myCoursesServiceNewStudent.generateContedMaterial(item.url ?? '');
    if(result) {
      item.url = data.url;
      setModalItem(item);
      setOpenSupplementaryModal(true);
    } else {
      setModalItem(null);
      setOpenSupplementaryModal(false);
    }
  };

  const handleCloseSupplementaryModal = () => {
    setOpenSupplementaryModal(false);
    setModalItem(null);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);

        const { result, data } = await myCoursesServiceNewStudent.getContent(
          courseId,
          disciplineId,
          contentId,
        );

        if (result) {
          setContent(data);
          setBlockedContent(false);

          const displayFiltered: IIdShouldDisplay = {};
          let count = 0;

          if (!Array.isArray(data)) {
            const resourceKeys = visibleResourceKeys ?? allResourceKeys;
            for (const key of resourceKeys) {
              const items = data[key as keyof IContentSubject];
              if (
                Array.isArray(items) &&
                getDisplayItems(key, items).length > 0
              ) {
                count += 1;
                displayFiltered[key] = count;
              }
            }
          }

          setContentShouldDisplay(displayFiltered);
        }
      } catch (error) {
        throw new Error(error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [courseId, disciplineId, contentId, visibleResourceKeys]);

  const isSelectedMaterial = (item: IDataContentSubject, type: string) => {
    if (!selectedContent || selectedContent.type !== type) {
      return false;
    }

    if (selectedContent.resourceId != null && item.resourceId != null) {
      return String(selectedContent.resourceId) === String(item.resourceId);
    }

    return (
      selectedContent.url === (item.url ?? '') &&
      selectedContent.title === item.title
    );
  };

  const itemInfoCollapse = (
    item: IDataContentSubject,
    index: number,
    type:
      | 'video'
      | 'archive'
      | 'audiobook'
      | 'customized'
      | 'externalLink'
      | 'tcc'
      | 'internship'
      | 'supplementaryMaterial'
      | 'assessmentWork'
      | 'evaluativeActivity',
    icon: ReactNode,
  ) => {
    return (
      <S.DivSubtitle key={index}>
        <S.DivSubtitleSeparated>
          {icon}
          <S.Subtitle
            $selected={isSelectedMaterial(item, type)}
            onClick={() => {
              if (type === 'supplementaryMaterial') {
                handleOpenSupplementaryModal(item);
              } else if (type === 'externalLink') {
                window.open(item.url, '_blank', 'noopener,noreferrer');
              } else {
                if (type === 'audiobook' && item.url) {
                  if (selectedAudioUrlRef.current !== item.url) {
                    selectedAudioUrlRef.current = item.url;
                    setIsLoading(true);
                  }
                } else {
                  selectedAudioUrlRef.current = undefined;
                }
                setSubjectContent!({
                  type: type,
                  url: item.url ?? '',
                  customized: item.customized,
                  title: item.title,
                  contentId: contentIdSelected,
                  resourceId: item.resourceId,
                  purpose: item.purpose,
                  purposeText: item.purposeText,
                });
              }
            }}
          >
            {item.title}
          </S.Subtitle>
        </S.DivSubtitleSeparated>
      </S.DivSubtitle>
    );
  };

  const unitTitleCollapse = (contentShouldDisplay, title) => {
    return (
      <S.DitTitle
        onClick={() => {
          unitOpen !== contentShouldDisplay
            ? setUnitOpen(contentShouldDisplay)
            : setUnitOpen(undefined);
        }}
      >
        {unitOpen !== contentShouldDisplay ? (
          <ArrowDropDownIcon fontSize="large" />
        ) : (
          <ArrowDropUpIcon fontSize="large" />
        )}
        <S.TitleName>{title}</S.TitleName>
      </S.DitTitle>
    );
  };

  const classTitleCollapse = (contentShouldDisplay, title) => {
    return (
      <S.DitTitle
        style={{ paddingLeft: '10px' }}
        onClick={() => {
          classOpen !== contentShouldDisplay
            ? setClassOpen(contentShouldDisplay)
            : setClassOpen(undefined);
        }}
      >
        {classOpen !== contentShouldDisplay ? (
          <ArrowDropDownIcon fontSize="large" />
        ) : (
          <ArrowDropUpIcon fontSize="large" />
        )}
        <S.TitleName>{title}</S.TitleName>
      </S.DitTitle>
    );
  };

  const itemTitleCollapse = (contentShouldDisplay, title) => {
    return (
      <S.DitTitle
        style={{ paddingLeft: '10px' }}
        onClick={() => {
          contentOpen !== contentShouldDisplay
            ? setContentOpen(contentShouldDisplay)
            : setContentOpen(undefined);
        }}
      >
        {contentOpen !== contentShouldDisplay ? (
          <ArrowDropDownIcon fontSize="large" />
        ) : (
          <ArrowDropUpIcon fontSize="large" />
        )}
        <S.TitleName>{title}</S.TitleName>
      </S.DitTitle>
    );
  };

  return (
    <>
      {openSupplementaryModal && modalItem && (
        <Modal
          open={openSupplementaryModal}
          onClose={handleCloseSupplementaryModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100vw',
              height: '100vh',
              bgcolor: 'background.paper',
              outline: 'none',
              p: 0,
              m: 0,
            }}
          >
            <S.ButtonClose onClick={handleCloseSupplementaryModal}>
              Fechar
            </S.ButtonClose>

            <iframe
              src={modalItem?.url}
              width="100%"
              height="100%"
              allow="autoplay"
              style={{ border: 'none' }}
            />
          </Box>
        </Modal>
      )}

      <S.Container>
        {!visibleResourceKeys && selectedContent?.title && (
          <S.SelectedMaterial>
            <S.SelectedLabel>Material selecionado</S.SelectedLabel>
            <S.SelectedName>{selectedContent.title}</S.SelectedName>
          </S.SelectedMaterial>
        )}
        {content && Array.isArray(content) ? (
          content?.map((unit, index) => (
            <S.ContentDiv key={index}>
              {unitTitleCollapse(index, unit.unitName)}
              {unitOpen === index &&
                unit.class?.map((cls, index2) => (
                  <S.ContentDiv key={index2}>
                    {classTitleCollapse(index2, cls.classroomName)}
                    {classOpen === index2 && (
                      <S.ContentDiv style={{ paddingLeft: '10px' }}>
                        {filterWithValidUrl(cls.resources?.video).length >
                          0 && (
                            <>
                              {itemTitleCollapse(0, 'Vídeos')}

                              {contentOpen === 0 &&
                                filterWithValidUrl(cls.resources?.video).map(
                                  (item: IDataContentSubject, index: number) => (
                                    <S.ContentDiv
                                      key={`video-${index}`}
                                      style={{ paddingLeft: '30px' }}
                                    >
                                      {itemInfoCollapse(
                                        item,
                                        index,
                                        'video',
                                        <OndemandVideoOutlinedIcon
                                          fontSize="large"
                                          style={{
                                            color: theme.colors?.text.danger,
                                          }}
                                        />,
                                      )}
                                    </S.ContentDiv>
                                  ))}
                            </>
                          )}
                        {filterWithValidUrl(cls.resources?.archive).length >
                          0 && (
                          <>
                            {itemTitleCollapse(1, 'Apostila')}
                            {contentOpen === 1 &&
                              filterWithValidUrl(cls.resources?.archive).map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`archive-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'archive',
                                      <DescriptionOutlinedIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {filterWithValidUrl(cls.resources?.audiobook).length >
                          0 && (
                          <>
                            {itemTitleCollapse(2, 'AudioBook')}
                            {contentOpen === 2 &&
                              filterWithValidUrl(cls.resources?.audiobook).map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`audiobook-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'audiobook',
                                      <GraphicEqIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {(cls.resources?.customized?.length ?? 0) > 0 && (
                          <>
                            {itemTitleCollapse(3, 'Conteúdo')}
                            {contentOpen === 3 &&
                              cls.resources?.customized?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`customized-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'customized',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {filterWithValidUrl(cls.resources?.externalLink)
                          .length > 0 && (
                          <>
                            {itemTitleCollapse(4, 'Externo')}
                            {contentOpen === 4 &&
                              filterWithValidUrl(
                                cls.resources?.externalLink,
                              ).map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`externalLink-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'externalLink',
                                      <OpenInNewOutlinedIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {filterWithValidUrl(
                          cls.resources?.supplementaryMaterial,
                        ).length > 0 && (
                          <>
                            {itemTitleCollapse(5, 'Material')}
                            {contentOpen === 5 &&
                              filterWithValidUrl(
                                cls.resources?.supplementaryMaterial,
                              ).map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`supplementaryMaterial-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'supplementaryMaterial',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {(cls.resources?.internship?.length ?? 0) > 0 && (
                          <>
                            {itemTitleCollapse(6, 'Estágio')}
                            {contentOpen === 6 &&
                              cls.resources?.internship?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`internship-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'internship',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {(cls.resources?.tcc?.length ?? 0) > 0 && (
                          <>
                            {itemTitleCollapse(7, 'TCC')}
                            {contentOpen === 7 &&
                              cls.resources?.tcc?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`tcc-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'tcc',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {getDisplayItems(
                          'assessmentWork',
                          cls.resources?.assessmentWork,
                        ).length > 0 && (
                          <>
                            {itemTitleCollapse(8, 'Trabalho Avaliativo')}
                            {contentOpen === 8 &&
                              getDisplayItems(
                                'assessmentWork',
                                cls.resources?.assessmentWork,
                              ).map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`assessmentWork-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'assessmentWork',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                        {getDisplayItems(
                          'evaluativeActivity',
                          cls.resources?.evaluativeActivity,
                        ).length > 0 && (
                          <>
                            {itemTitleCollapse(9, 'Atividade Avaliativa')}
                            {contentOpen === 9 &&
                              getDisplayItems(
                                'evaluativeActivity',
                                cls.resources?.evaluativeActivity,
                              ).map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`evaluativeActivity-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'evaluativeActivity',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        )}
                      </S.ContentDiv>
                    )}
                  </S.ContentDiv>
                ))}
            </S.ContentDiv>
          ))
        ) : (
          <>
            {contentShouldDisplay?.video && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.video, 'Vídeos')}
                {contentOpen === contentShouldDisplay?.video &&
                  filterWithValidUrl(
                    (content as IContentSubject)?.video,
                  ).map((item: IDataContentSubject, index: number) =>
                    itemInfoCollapse(
                      item,
                      index,
                      'video',
                      <OndemandVideoOutlinedIcon
                        fontSize="large"
                        style={{
                          color: theme.colors?.text.danger,
                        }}
                      />,
                    ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.archive && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.archive, 'Apostila')}
                {contentOpen === contentShouldDisplay?.archive &&
                  filterWithValidUrl(
                    (content as IContentSubject)?.archive,
                  ).map((item: IDataContentSubject, index: number) =>
                    itemInfoCollapse(
                      item,
                      index,
                      'archive',
                      <DescriptionOutlinedIcon
                        fontSize="large"
                        style={{
                          color: theme.colors?.text.danger,
                        }}
                      />,
                    ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.audiobook && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.audiobook, 'AudioBook')}
                {contentOpen === contentShouldDisplay?.audiobook &&
                  filterWithValidUrl(
                    (content as IContentSubject)?.audiobook,
                  ).map((item: IDataContentSubject, index: number) =>
                    itemInfoCollapse(
                      item,
                      index,
                      'audiobook',
                      <GraphicEqIcon
                        fontSize="large"
                        style={{
                          color: theme.colors?.text.danger,
                        }}
                      />,
                    ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.customized && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.customized, 'Conteúdo')}
                {contentOpen === contentShouldDisplay?.customized &&
                  ((content as IContentSubject)?.customized ?? []).map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'customized',
                        <GraphicEqIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.externalLink && (
              <>
                {itemTitleCollapse(contentShouldDisplay?.externalLink, 'Externo')}
                {contentOpen === contentShouldDisplay?.externalLink &&
                  filterWithValidUrl(
                    (content as IContentSubject)?.externalLink,
                  ).map((item: IDataContentSubject, index: number) => (
                    <S.ContentDiv
                      key={`externalLink-${index}`}
                      style={{ paddingLeft: '30px' }}
                    >
                      {itemInfoCollapse(
                        item,
                        index,
                        'externalLink',
                        <OpenInNewOutlinedIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      )}
                    </S.ContentDiv>
                  ))}
              </>
            )}
            {contentShouldDisplay?.internship && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.internship, 'Estágio')}
                {contentOpen === contentShouldDisplay?.internship &&
                  ((content as IContentSubject)?.internship ?? []).map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'internship',
                        <OutboxIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.tcc && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.tcc, 'TCC')}
                {contentOpen === contentShouldDisplay?.tcc &&
                  ((content as IContentSubject)?.tcc ?? []).map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'tcc',
                        <OutboxIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.supplementaryMaterial && (
              <S.ContentDiv>
                {itemTitleCollapse(
                  contentShouldDisplay.supplementaryMaterial,
                  'Material',
                )}
                {contentOpen === contentShouldDisplay?.supplementaryMaterial &&
                  filterWithValidUrl(
                    (content as IContentSubject)?.supplementaryMaterial,
                  ).map((item: IDataContentSubject, index: number) =>
                    itemInfoCollapse(
                      item,
                      index,
                      'supplementaryMaterial',
                      <OutboxIcon
                        fontSize="large"
                        style={{
                          color: theme.colors?.text.danger,
                        }}
                      />,
                    ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.assessmentWork && (
              <S.ContentDiv>
                {itemTitleCollapse(
                  contentShouldDisplay.assessmentWork,
                  'Trabalho Avaliativo',
                )}
                {contentOpen === contentShouldDisplay?.assessmentWork &&
                  getDisplayItems(
                    'assessmentWork',
                    (content as IContentSubject)?.assessmentWork,
                  ).map((item: IDataContentSubject, index: number) =>
                    itemInfoCollapse(
                      item,
                      index,
                      'assessmentWork',
                      <OutboxIcon
                        fontSize="large"
                        style={{
                          color: theme.colors?.text.danger,
                        }}
                      />,
                    ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.evaluativeActivity && (
              <S.ContentDiv>
                {itemTitleCollapse(
                  contentShouldDisplay.evaluativeActivity,
                  'Atividade Avaliativa',
                )}
                {contentOpen === contentShouldDisplay?.evaluativeActivity &&
                  getDisplayItems(
                    'evaluativeActivity',
                    (content as IContentSubject)?.evaluativeActivity,
                  ).map((item: IDataContentSubject, index: number) =>
                    itemInfoCollapse(
                      item,
                      index,
                      'evaluativeActivity',
                      <OutboxIcon
                        fontSize="large"
                        style={{
                          color: theme.colors?.text.danger,
                        }}
                      />,
                    ),
                  )}
              </S.ContentDiv>
            )}
          </>
        )}
      </S.Container>
    </>
  );
}
